// TODO loginGuard etc can go to parent routes?

import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
} from "vue-router";

import useUser from "@/composables/user";
import useConfig from "@/composables/config";

const loginGuard = (to: RouteLocationNormalized) => {
  const { user } = useUser();
  if (!user.isLoggedIn) {
    return {
      name: "Login",
      query: { returnUrl: to.path, returnParams: JSON.stringify(to.query) },
    };
  }
};

const adminGuard = (adminPermissions: string[] = []) => {
  return (to: RouteLocationNormalized) => {
    const { user } = useUser();
    if (!user.isAdmin || !user.current?.isAuthorizedFor(adminPermissions)) {
      return { name: "403" };
    }
  };
};

const contentAdminGuard = (adminPermissions: string[] = []) => {
  return (to: RouteLocationNormalized) => {
    const { user } = useUser();
    if (
      !user.isContentAdmin ||
      !user.current?.isAuthorizedFor(adminPermissions)
    ) {
      return { name: "403" };
    }
  };
};

const configAdminGuard = (adminPermissions: string[] = []) => {
  return (to: RouteLocationNormalized) => {
    const { user } = useUser();
    if (
      !user.isConfigAdmin ||
      !user.current?.isAuthorizedFor(adminPermissions)
    ) {
      return { name: "403" };
    }
  };
};

const orcidGuard = (to: RouteLocationNormalized) => {
  const { config } = useConfig();
  if (!config.orcidEnabled) {
    return { name: "403" };
  }
};

const userHasPersonGuard = (to: RouteLocationNormalized) => {
  const { user } = useUser();
  const { config } = useConfig();
  if (!user.current?.person_id) {
    window.location.href = config.freidokBaseUrl + "/myFreidok/profile";
    return false;
  }
};

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/",
      name: "Home",
      // component: () => import("@/views/HomeViewRedirect.vue"),
      component: () => import("@/views/HomeView.vue"),
    },
    // {
    //   path: "/home",
    //   name: "NewHome",
    //   component: () => import("@/views/HomeView.vue"),
    // },
    {
      path: "/site/help",
      name: "Help",
      component: () => import("@/views/HelpView.vue"),
    },
    {
      path: "/site/:type",
      name: "Site",
      props: true,
      component: () => import("@/views/WebtextView.vue"),
    },
    {
      path: "/403",
      name: "403",
      component: () => import("@/views/403Forbidden.vue"),
    },
    {
      path: "/login",
      name: "Login",
      component: () => import("@/views/LoginView.vue"),
    },
    {
      path: "/report",
      redirect: () => ({ name: "ReportInstitution" }),
      beforeEnter: [loginGuard],
      children: [
        {
          path: "institution",
          name: "ReportInstitution",
          props: { type: "institution" },
          component: () => import("@/views/ReportView.vue"),
        },
        {
          path: "person",
          name: "ReportPerson",
          props: { type: "person" },
          component: () => import("@/views/ReportView.vue"),
        },
      ],
    },
    {
      path: "/rs-gallery",
      name: "RsGallery",
      component: () => import("@/views/RsGalleryView.vue"),
    },
    {
      path: "/admin",
      component: () => import("@/views/admin/AdminView.vue"),
      redirect: () => ({ name: "AdminSearchPermissions" }),
      children: [
        {
          path: "permissions",
          name: "AdminSearchPermissions",
          component: () =>
            import("@/views/admin/AdminSearchPermissionsView.vue"),
          beforeEnter: [loginGuard, contentAdminGuard()],
        },
        {
          path: "publications",
          name: "AdminSearchPublications",
          component: () =>
            import("@/views/admin/AdminSearchPublicationsView.vue"),
          beforeEnter: [loginGuard, adminGuard()],
        },
        {
          path: "publications/:id",
          name: "AdminEditPublication",
          props: true,
          component: () => import("@/views/admin/AdminEditPublicationView.vue"),
          beforeEnter: [loginGuard, adminGuard()],
        },
        {
          path: "persons",
          name: "AdminSearchPersons",
          component: () => import("@/views/admin/AdminSearchPersonsView.vue"),
          beforeEnter: [loginGuard, adminGuard()],
        },
        {
          path: "persons/:id",
          name: "AdminEditPerson",
          props: true,
          component: () => import("@/views/admin/AdminEditPersonView.vue"),
          beforeEnter: [loginGuard, adminGuard()],
        },
        {
          path: "projects",
          name: "AdminSearchProjects",
          component: () => import("@/views/admin/AdminSearchProjectsView.vue"),
          beforeEnter: [loginGuard, adminGuard()],
        },
        {
          path: "projects/:id",
          name: "AdminEditProject",
          props: true,
          component: () => import("@/views/admin/AdminEditProjectView.vue"),
          beforeEnter: [loginGuard, adminGuard()],
        },
        {
          path: "activities",
          name: "AdminSearchActivities",
          component: () =>
            import("@/views/admin/AdminSearchActivitiesView.vue"),
          beforeEnter: [loginGuard, adminGuard()],
        },
        {
          path: "activities/:id",
          name: "AdminEditActivity",
          props: true,
          component: () => import("@/views/admin/AdminEditActivityView.vue"),
          beforeEnter: [loginGuard, adminGuard()],
        },
        {
          path: "institutions",
          name: "AdminSearchInstitutions",
          component: () =>
            import("@/views/admin/AdminSearchInstitutionsView.vue"),
          beforeEnter: [loginGuard, adminGuard()],
        },
        {
          path: "institutions/:id",
          name: "AdminEditInstitution",
          props: true,
          component: () => import("@/views/admin/AdminEditInstitutionView.vue"),
          beforeEnter: [loginGuard, adminGuard()],
        },
        {
          path: "webtexts",
          name: "AdminSearchWebtexts",
          component: () => import("@/views/admin/AdminSearchWebtextsView.vue"),
          beforeEnter: [loginGuard, contentAdminGuard()],
        },
        {
          path: "webtexts/:id",
          name: "AdminEditWebtext",
          props: true,
          component: () => import("@/views/admin/AdminEditWebtextView.vue"),
          beforeEnter: [loginGuard, contentAdminGuard()],
        },
        {
          path: "orcid-import-test",
          name: "AdminOrcidImportTest",
          component: () => import("@/views/admin/OrcidImportTest.vue"),
          beforeEnter: [loginGuard, configAdminGuard()],
        },
        {
          path: "merge/persons",
          name: "AdminMergePersons",
          component: () =>
            import("@/views/admin/MergePersonsView/MergePersonsView.vue"),
          beforeEnter: [loginGuard, adminGuard()],
        },
      ],
    },
    {
      path: "/edit",
      redirect: () => ({ name: "UserSearchPublications" }),
      beforeEnter: [loginGuard],
      children: [
        {
          path: "publications",
          name: "UserSearchPublications",
          component: () =>
            import("@/views/edit/UserSearchPublicationsView.vue"),
        },
        {
          path: "projects",
          name: "UserSearchProjects",
          component: () => import("@/views/edit/UserSearchProjectsView.vue"),
        },
        {
          path: "activities",
          name: "UserSearchActivities",
          component: () => import("@/views/edit/UserSearchActivitiesView.vue"),
        },
        {
          path: "persons",
          name: "UserSearchPersons",
          component: () => import("@/views/edit/UserSearchPersonsView.vue"),
        },
        {
          path: "institutions",
          name: "UserSearchInstitutions",
          component: () =>
            import("@/views/edit/UserSearchInstitutionsView.vue"),
        },
        {
          path: "permissions",
          name: "UserSearchPermissions",
          component: () => import("@/views/edit/UserSearchPermissionsView.vue"),
        },
        {
          path: "projects/:id",
          name: "EditProject",
          props: true,
          component: () => import("@/views/edit/EditProjectView.vue"),
        },
        {
          path: "publications/:id",
          name: "EditPublication",
          props: true,
          component: () => import("@/views/edit/EditPublicationView.vue"),
        },
        {
          path: "activities/:id",
          name: "EditActivity",
          props: true,
          component: () => import("@/views/edit/EditActivityView.vue"),
        },
        {
          path: "persons/:id",
          name: "EditPerson",
          props: true,
          component: () => import("@/views/edit/EditPersonView.vue"),
        },
        {
          path: "institutions/:id",
          name: "EditInstitution",
          props: true,
          component: () => import("@/views/edit/EditInstitutionView.vue"),
        },
      ],
    },
    {
      path: "/search",
      redirect: () => ({ name: "SearchPublications" }),
      children: [
        {
          path: "publications",
          name: "SearchPublications",
          component: () => import("@/views/search/SearchPublicationsView.vue"),
        },
        {
          path: "projects",
          name: "SearchProjects",
          component: () => import("@/views/search/SearchProjectsView.vue"),
        },
        {
          path: "persons",
          name: "SearchPersons",
          component: () => import("@/views/search/SearchPersonsView.vue"),
        },
        {
          path: "institutions",
          name: "SearchInstitutions",
          component: () => import("@/views/search/SearchInstitutionsView.vue"),
        },
      ],
    },
    {
      path: "/import/doi",
      name: "ImportDoi",
      component: () => import("@/views/import/DoiImportView/DoiImportView.vue"),
      beforeEnter: [loginGuard],
    },
    {
      path: "/orcid",
      name: "OrcidOverview",
      component: () =>
        import("@/views/orcid/OrcidOverviewView/OrcidOverviewView.vue"),
      beforeEnter: [orcidGuard, loginGuard, userHasPersonGuard],
    },
    {
      path: "/orcid/import",
      name: "OrcidImport",
      component: () =>
        import("@/views/orcid/OrcidImportView/OrcidImportView.vue"),
      beforeEnter: [orcidGuard, loginGuard, userHasPersonGuard],
    },
    {
      path: "/orcid/export",
      name: "OrcidExport",
      component: () =>
        import("@/views/orcid/OrcidExportView/OrcidExportView.vue"),
      beforeEnter: [orcidGuard, loginGuard, userHasPersonGuard],
    },
    {
      path: "/submit/activity",
      name: "SubmitActivity",
      props: true,
      component: () => import("@/views/submit/SubmitActivityView.vue"),
      beforeEnter: [loginGuard],
    },
    {
      path: "/:pathMatch(.*)*",
      name: "404",
      component: () => import("@/views/404NotFound.vue"),
    },
  ],
});

export default router;
